
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
/**
 * Secure Recent Search Service
 * Uses backend API endpoints instead of direct Firestore access
 * Now supports facility-specific recent searches
 */

export const addRecentSearch = async (userId, patientData, facilityId) => {
  try {
    console.log('Adding recent search via API:', userId, patientData.id, 'for facility:', facilityId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/internal/users/${userId}/recent-searches`,
      {
        patientData: {
          id: patientData.id,
          name: patientData.name,
          mrn: patientData.mrn
        },
        facilityId
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data.recentSearches;
  } catch (error) {
    console.error('Error adding recent search:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const getRecentSearches = async (userId, facilityId) => {
  try {
    console.log('Getting recent searches via API:', userId, 'for facility:', facilityId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/internal/users/${userId}/recent-searches?facilityId=${facilityId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data.recentSearches;
  } catch (error) {
    console.error('Error getting recent searches:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return [];
    }
    return [];
  }
};

export const clearRecentSearches = async (userId, facilityId) => {
  try {
    console.log('Clearing recent searches via API:', userId, 'for facility:', facilityId);
    const token = await getAuthToken();
    
    await axios.delete(
      `${API_BASE}/internal/users/${userId}/recent-searches?facilityId=${facilityId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
  } catch (error) {
    console.error('Error clearing recent searches:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

/**
 * Get the current user's recent searches for a specific facility
 */
export const getCurrentUserRecentSearches = async (facilityId, userId) => {
  try {
    if (!userId) {
      console.warn('No user ID provided for recent searches');
      return [];
    }
    
    if (!facilityId) {
      console.warn('No facility ID provided for recent searches');
      return [];
    }
    
    return await getRecentSearches(userId, facilityId);
  } catch (error) {
    console.error('Error getting current user recent searches:', error);
    return [];
  }
};

/**
 * Add a recent search for the current user in a specific facility
 */
export const addCurrentUserRecentSearch = async (patientData, facilityId, userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required for recent searches');
    }
    
    if (!facilityId) {
      throw new Error('Facility ID is required for recent searches');
    }
    
    return await addRecentSearch(userId, patientData, facilityId);
  } catch (error) {
    console.error('Error adding current user recent search:', error);
    throw error;
  }
};

/**
 * Clear recent searches for the current user in a specific facility
 */
export const clearCurrentUserRecentSearches = async (facilityId, userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required for recent searches');
    }
    
    if (!facilityId) {
      throw new Error('Facility ID is required for recent searches');
    }
    
    await clearRecentSearches(userId, facilityId);
  } catch (error) {
    console.error('Error clearing current user recent searches:', error);
    throw error;
  }
};

/**
 * Add a patient to recent searches with automatic user detection and facility context
 */
export const addPatientToRecentSearches = async (patient, facilityId, userId) => {
  try {
    if (!userId) {
      console.warn('No user ID provided, cannot add to recent searches');
      return [];
    }
    
    if (!facilityId) {
      console.warn('No facility ID provided, cannot add to recent searches');
      return [];
    }
    
    const patientData = {
      id: patient.id,
      name: patient.name || `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || patient.id,
      mrn: patient.mrn || patient.medicalRecordNumber || 'Unknown'
    };
    
    return await addCurrentUserRecentSearch(patientData, facilityId, userId);
  } catch (error) {
    console.error('Error adding patient to recent searches:', error);
    // Don't throw - this is a convenience feature
    return [];
  }
};

/**
 * Search recent searches by name or MRN within a specific facility
 */
export const searchRecentSearches = async (searchTerm, facilityId, userId) => {
  try {
    const targetUserId = userId;

    if (!targetUserId || !facilityId) {
      return [];
    }
    
    const recentSearches = await getRecentSearches(targetUserId, facilityId);
    
    if (!searchTerm || searchTerm.trim() === '') {
      return recentSearches;
    }
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    return recentSearches.filter(search => 
      (search.name && search.name.toLowerCase().includes(lowerSearchTerm)) ||
      (search.mrn && search.mrn.toLowerCase().includes(lowerSearchTerm))
    );
  } catch (error) {
    console.error('Error searching recent searches:', error);
    return [];
  }
};

/**
 * Get recent searches with enhanced data for a specific facility
 */
export const getEnhancedRecentSearches = async (facilityId, userId) => {
  try {
    const targetUserId = userId;
    if (!targetUserId || !facilityId) {
      return [];
    }
    
    const recentSearches = await getRecentSearches(targetUserId, facilityId);
    
    // Enhance with additional metadata
    return recentSearches.map(search => ({
      ...search,
      searchedAtFormatted: new Date(search.searchedAt).toLocaleDateString(),
      searchedAtRelative: getRelativeTime(new Date(search.searchedAt)),
      initials: getInitials(search.name)
    }));
  } catch (error) {
    console.error('Error getting enhanced recent searches:', error);
    return [];
  }
};

/**
 * Remove a specific search from recent searches in a specific facility
 */
export const removeRecentSearch = async (patientId, facilityId, userId) => {
  try {
    const targetUserId = userId;
    if (!targetUserId || !facilityId) {
      return [];
    }
    
    const recentSearches = await getRecentSearches(targetUserId, facilityId);
    const filteredSearches = recentSearches.filter(search => search.id !== patientId);
    
    // We'll need to manually update via the clear and re-add approach
    // since we don't have a specific remove endpoint
    await clearRecentSearches(targetUserId, facilityId);
    
    // Re-add the remaining searches
    for (const search of filteredSearches) {
      await addRecentSearch(targetUserId, search, facilityId);
    }
    
    return filteredSearches;
  } catch (error) {
    console.error('Error removing recent search:', error);
    throw error;
  }
};

/**
 * Get statistics about recent searches for a specific facility
 */
export const getRecentSearchStats = async (facilityId, userId) => {
  try {
    const targetUserId = userId;
    if (!targetUserId || !facilityId) {
      return {
        total: 0,
        lastSearchDate: null,
        uniquePatients: 0
      };
    }
    
    const recentSearches = await getRecentSearches(targetUserId, facilityId);
    
    const stats = {
      total: recentSearches.length,
      lastSearchDate: recentSearches.length > 0 ? recentSearches[0].searchedAt : null,
      uniquePatients: new Set(recentSearches.map(search => search.id)).size
    };
    
    return stats;
  } catch (error) {
    console.error('Error getting recent search stats:', error);
    return {
      total: 0,
      lastSearchDate: null,
      uniquePatients: 0
    };
  }
};

// Helper functions

/**
 * Get relative time string
 */
function getRelativeTime(date) {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

/**
 * Get initials from name
 */
function getInitials(name) {
  if (!name) return '??';
  
  const nameParts = name.trim().split(' ');
  if (nameParts.length === 1) {
    return nameParts[0].substring(0, 2).toUpperCase();
  }
  
  return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
}
